import {
  ProfessionalCard,
  ProfessionalCardSkeleton
} from '@features/professionals/components'
import { Button } from '@shared/components/ui/button'
import type { Professional } from '@shared/types'
import { AlertCircle, Search } from 'lucide-react'

interface ProfessionalsGridProps {
  professionals?: Professional[]
  isLoading: boolean
  error?: Error | null
  onProfessionalClick: (professional: Professional) => void
  onContactClick: (professional: Professional) => void
  hasFiltersApplied: boolean
  onClearFilters?: () => void
}

export function ProfessionalsGrid({
  professionals,
  isLoading,
  error,
  onProfessionalClick,
  hasFiltersApplied,
  onClearFilters
}: ProfessionalsGridProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-6 mb-8">
        {Array.from({ length: 6 }).map((_, i) => (
          <ProfessionalCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="mb-8">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            Error al cargar profesionales
          </h2>
          <p className="text-muted-foreground mb-6">
            Hubo un problema al obtener la lista de profesionales.
          </p>
          <Button onClick={() => window.location.reload()}>
            Reintentar
          </Button>
        </div>
      </div>
    )
  }

  // Empty state
  if (!professionals || professionals.length === 0) {
    return (
      <div className="mb-8">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <Search className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            No se encontraron profesionales
          </h2>
          <p className="text-muted-foreground mb-6">
            {hasFiltersApplied
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'Aún no hay profesionales registrados en esta categoría'}
          </p>
          {hasFiltersApplied && (
            <Button variant="outline" onClick={onClearFilters}>
              Limpiar filtros
            </Button>
          )}
        </div>
      </div>
    )
  }

  // Success state
  return (
    <div className="grid grid-cols-1 gap-6 mb-8" data-tour="professionals-grid">
      {professionals.map((professional, index) => (
        <ProfessionalCard
          key={professional.id}
          professional={professional}
          onClick={onProfessionalClick}
          data-tour={index === 0 ? 'professional-card-0' : undefined}
        />
      ))}
    </div>
  )
}
